import { offeredDivisors, renderLayout, resolveDivisor, uiScaleMost, uiScaleOf, type InterfaceStep, type RenderDivisor, type Size } from "odyssey-shared";
import { type SettingsStore } from "./settings.ts";
import { writeInterfaceStep, writeRenderDivisor } from "./display-settings.ts";
import type { DeviceView } from "./viewport.ts";

/**
 * The page's render resolution (ADR-093 decision 10, CLIENT.md 7.1): the viewer's stored divisor resolved against
 * what the window offers, and the view everything else on the page is laid out from. At Full the view is the
 * window in device pixels. At Half or Third it is the render size, with the render pixels a CSS pixel in place of
 * the device pixel ratio, so the overlay's `--px`, the HUD's layout, the UI scale and the canvas's backing store
 * all follow from one view and the DOM's logical pixel stays the canvas's. The canvas is stretched back over the
 * window by the stylesheet, with nearest sampling, at exactly the divisor in device pixels a render pixel.
 *
 * A resize, browser zoom or a move to a screen of another pixel ratio is a new device view: the offer is weighed
 * again, and a divisor the window no longer offers falls back to the largest it does, Full when none, without the
 * stored choice being rewritten. `?render=` in the benchmark mode is an override that is never stored.
 */

/**
 * The custom properties on the canvas's host that size the canvas in CSS pixels; unset at Full, where the
 * stylesheet's 100 percent applies.
 */
export const CANVAS_WIDTH_PROPERTY = "--canvas-width";
export const CANVAS_HEIGHT_PROPERTY = "--canvas-height";

/**
 * Reads `?render=` as a divisor.
 *
 * @param raw The parameter's value, or null when the page has none.
 * @returns 1, 2 or 3, or null for anything else.
 */
export function parseRenderOverride(raw: string | null): RenderDivisor | null {
  return raw === "1" ? 1 : raw === "2" ? 2 : raw === "3" ? 3 : null;
}

/**
 * The view a window is laid out from at a divisor.
 *
 * @param device The window in device pixels and its pixel ratio.
 * @param divisor The divisor in force.
 * @returns The device view itself at Full; above it the render size with the render pixels a CSS pixel.
 */
export function renderView(device: DeviceView, divisor: RenderDivisor): DeviceView {
  if (divisor === 1) {
    return device;
  }
  const layout = renderLayout(device, device.ratio, divisor);
  return { width: layout.size.width, height: layout.size.height, ratio: layout.ratio, aboveFull: true };
}

/**
 * A view carrying the viewer's Interface size, left out at Auto so a view at Auto is the plain one.
 *
 * @param view The view.
 * @param step The step.
 * @returns The view with its `step`, or the view itself at 0.
 */
function withStep(view: DeviceView, step: InterfaceStep): DeviceView {
  return step === 0 ? view : { ...view, step };
}

/**
 * The canvas's size in CSS pixels at a divisor: each render pixel exactly the divisor in device pixels, so the
 * canvas may pass the window by less than the divisor in device pixels, which the host clips.
 *
 * @param view The view the page is laid out from, as `renderView` gives it.
 * @param divisor The divisor in force.
 * @returns The width and height as CSS lengths, or null at Full, where the canvas fills its host.
 */
export function canvasCssSize(view: DeviceView, divisor: RenderDivisor): { readonly width: string; readonly height: string } | null {
  return divisor === 1 ? null : { width: `${view.width / view.ratio}px`, height: `${view.height / view.ratio}px` };
}

/**
 * Sizes the canvas through its host's custom properties.
 *
 * @param host The element the canvas fills.
 * @param view The view the page is laid out from.
 * @param divisor The divisor in force.
 */
export function applyCanvasSize(host: HTMLElement, view: DeviceView, divisor: RenderDivisor): void {
  const size = canvasCssSize(view, divisor);
  if (size === null) {
    host.style.removeProperty(CANVAS_WIDTH_PROPERTY);
    host.style.removeProperty(CANVAS_HEIGHT_PROPERTY);
    return;
  }
  host.style.setProperty(CANVAS_WIDTH_PROPERTY, size.width);
  host.style.setProperty(CANVAS_HEIGHT_PROPERTY, size.height);
}

/**
 * The page's render resolution: the window, the stored divisor, the override, and whoever follows the result.
 */
export class RenderResolution {
  private deviceNow: DeviceView;
  private storedNow: RenderDivisor;
  private overrideNow: RenderDivisor | null;
  private divisorNow: RenderDivisor;
  private viewNow: DeviceView;
  private stepNow: InterfaceStep;
  private readonly listeners = new Set<() => void>();

  /**
   * @param device The window in device pixels and its pixel ratio.
   * @param stored The viewer's stored divisor, as `readRenderDivisor` gives it.
   * @param override The page's override, or null.
   * @param store Where a new choice is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    device: DeviceView,
    stored: RenderDivisor,
    override: RenderDivisor | null = null,
    private readonly store?: SettingsStore | null,
    step: InterfaceStep = 0,
  ) {
    this.stepNow = step;
    this.deviceNow = device;
    this.storedNow = stored;
    this.overrideNow = override;
    this.divisorNow = resolveDivisor(override ?? stored, device);
    this.viewNow = withStep(renderView(device, this.divisorNow), step);
  }

  /**
   * The divisor in force: the override or the stored choice, resolved against what the window offers.
   *
   * @returns The divisor.
   */
  get divisor(): RenderDivisor {
    return this.divisorNow;
  }

  /**
   * The viewer's stored choice, whatever the window offers.
   *
   * @returns The divisor.
   */
  get stored(): RenderDivisor {
    return this.storedNow;
  }

  /**
   * The window in device pixels and its pixel ratio.
   *
   * @returns The device view.
   */
  get device(): DeviceView {
    return this.deviceNow;
  }

  /**
   * The view the page is laid out from.
   *
   * @returns The device view at Full, the render view above it.
   */
  get view(): DeviceView {
    return this.viewNow;
  }

  /**
   * The divisors the window offers now.
   *
   * @returns The offered divisors, Full first.
   */
  get offered(): readonly RenderDivisor[] {
    return offeredDivisors(this.deviceNow);
  }

  /**
   * Takes a new device view and weighs the offer again.
   *
   * @param device The window in device pixels and its pixel ratio.
   */
  resize(device: DeviceView): void {
    const before = this.deviceNow;
    this.deviceNow = device;
    this.update(before.width !== device.width || before.height !== device.height || before.ratio !== device.ratio || before.touch !== device.touch);
  }

  /**
   * Takes the viewer's new choice, keeps it, and puts it in force as far as the window offers it.
   *
   * @param divisor The divisor chosen.
   */
  setDivisor(divisor: RenderDivisor): void {
    this.storedNow = divisor;
    writeRenderDivisor(divisor, this.store);
    this.update(false);
  }

  /**
   * The viewer's Interface size, a whole step from the automatic UI scale.
   *
   * @returns The step.
   */
  get interfaceStep(): InterfaceStep {
    return this.stepNow;
  }

  /**
   * The UI scale a step would give the page now, as the view in force draws it: what Settings shows beside the row
   * and what decides which steps change anything.
   *
   * @param step The step.
   * @returns The whole UI scale.
   */
  scaleAt(step: InterfaceStep): number {
    return uiScaleOf({ ...this.viewNow, step });
  }

  /**
   * The largest whole UI scale the window holds at the divisor in force.
   *
   * @returns The scale.
   */
  get mostScale(): number {
    const size: Size = this.viewNow;
    return Math.max(this.scaleAt(0), uiScaleMost(size.width, size.height));
  }

  /**
   * Takes the viewer's Interface size, keeps it, and puts it in force at once: the overlay, the HUD and the UI camera
   * follow through the same call as a new window size.
   *
   * @param step The step chosen.
   */
  setInterfaceStep(step: InterfaceStep): void {
    if (step === this.stepNow) {
      return;
    }
    this.stepNow = step;
    writeInterfaceStep(step, this.store);
    this.update(true);
  }

  /**
   * Sets or clears the page's override, which decides the divisor while it stands and is never stored.
   *
   * @param override The divisor to force as far as the window offers it, or null for the stored choice.
   */
  setOverride(override: RenderDivisor | null): void {
    this.overrideNow = override;
    this.update(false);
  }

  /**
   * Calls back whenever the window or the divisor in force changes.
   *
   * @param listener Called after the change; it reads the new values from this object.
   * @returns A function that stops the calls.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Resolves the divisor and the view again and tells the listeners when the window or the divisor moved.
   *
   * @param moved Whether the window itself changed.
   */
  private update(moved: boolean): void {
    const divisor = resolveDivisor(this.overrideNow ?? this.storedNow, this.deviceNow);
    if (!moved && divisor === this.divisorNow) {
      return;
    }
    this.divisorNow = divisor;
    this.viewNow = withStep(renderView(this.deviceNow, divisor), this.stepNow);
    for (const listener of this.listeners) {
      listener();
    }
  }
}
